import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import dicomImageLoader from '@cornerstonejs/dicom-image-loader';
import { DicomMetadataStore, MODULE_TYPES, useSystem } from '@ohif/core';

import Dropzone from 'react-dropzone';
import filesToStudies from './filesToStudies';
import { revokeAllLocalObjectUrls } from './localObjectUrlRegistry';

import { extensionManager } from '../../App';

/**
 * File / folder pickers must not nest `<input type="file">` inside `<button>` (invalid HTML).
 */
const getLoadButton = (
  onChooseFiles: (acceptedFiles: File[]) => void,
  text: string,
  isDir: boolean
) => {
  return (
    <Dropzone
      onDrop={onChooseFiles}
      noDrag
    >
      {({ getRootProps, getInputProps }) => (
        <div
          {...getRootProps({
            className:
              'inline-flex h-36 w-full cursor-pointer items-center justify-center rounded-lg border-2 border-green-500 bg-neutral-900 px-16 text-4xl font-medium leading-tight text-green-400 shadow-md transition-colors hover:border-green-400 hover:bg-neutral-800 hover:text-green-300 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-green-500 focus-visible:ring-offset-4 focus-visible:ring-offset-black sm:text-5xl',
          })}
        >
          <input
            {...getInputProps(
              isDir
                ? ({
                    multiple: true,
                    webkitdirectory: '',
                    mozdirectory: '',
                    directory: '',
                  } as React.InputHTMLAttributes<HTMLInputElement>)
                : { multiple: true }
            )}
          />
          <span className="text-white">{text}</span>
        </div>
      )}
    </Dropzone>
  );
};

type LocalProps = {
  modePath: string;
};

function Local({ modePath }: LocalProps) {
  const { servicesManager } = useSystem();
  const { customizationService } = servicesManager.services;
  const navigate = useNavigate();
  const dropzoneRef = useRef(null);
  const [pendingFiles, setPendingFiles] = React.useState<File[]>([]);
  const [loading, setLoading] = React.useState(false);

  const LoadingIndicatorProgress = customizationService?.getCustomization(
    'ui.loadingIndicatorProgress'
  ) as React.ComponentType<{ className?: string }> | undefined;

  const dataSourceModules = extensionManager.modules[MODULE_TYPES.DATA_SOURCE];
  const localDataSources = dataSourceModules.reduce((acc: any[], curr: any) => {
    const mods: any[] = [];
    curr.module.forEach((mod: any) => {
      if (mod.type === 'localApi') {
        mods.push(mod);
      }
    });
    return acc.concat(mods);
  }, []);

  const firstLocalDataSource = localDataSources[0];
  const dataSource = firstLocalDataSource.createDataSource({});

  const microscopyExtensionLoaded = extensionManager.registeredExtensionIds.includes(
    '@ohif/extension-dicom-microscopy'
  );

  const addChosenFiles = (acceptedFiles: File[]) => {
    if (!acceptedFiles?.length) {
      return;
    }
    setPendingFiles(acceptedFiles);
  };

  const loadIntoOhifLocal = async () => {
    if (!pendingFiles.length) {
      return;
    }

    setLoading(true);
    try {
      revokeAllLocalObjectUrls();
      DicomMetadataStore.clearStudies();
      dicomImageLoader.wadouri.dataSetCacheManager.purge();
      dicomImageLoader.wadouri.fileManager.purge();

      const studies = await filesToStudies(pendingFiles, dataSource);

      const query = new URLSearchParams();
      let targetPath = modePath;

      if (microscopyExtensionLoaded) {
        const smStudies = studies.filter(id => {
          const study = DicomMetadataStore.getStudy(id);
          return (
            study.series.findIndex(
              s => s.Modality === 'SM' || s.instances[0].Modality === 'SM'
            ) >= 0
          );
        });

        if (smStudies.length > 0) {
          smStudies.forEach(id => query.append('StudyInstanceUIDs', id));
          targetPath = 'microscopy';
        }
      }

      if (targetPath === 'microscopy') {
        studies.forEach(id => query.append('StudyInstanceUIDs', id));
      } else if (targetPath === 'viewer/dicomlocal' && studies.length > 0) {
        query.append('StudyInstanceUIDs', studies[0]);
      } else {
        studies.forEach(id => query.append('StudyInstanceUIDs', id));
      }
      query.append('datasources', 'dicomlocal');

      navigate(`/${targetPath}?${decodeURIComponent(query.toString())}`);
    } catch (e) {
      console.error('[OHIF Local]', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.body.classList.add('bg-neutral-950');
    return () => {
      document.body.classList.remove('bg-neutral-950');
    };
  }, []);

  return (
    <Dropzone
      ref={dropzoneRef}
      onDrop={acceptedFiles => {
        if (!acceptedFiles?.length) {
          return;
        }
        addChosenFiles(acceptedFiles);
      }}
      noClick
    >
      {({ getRootProps }) => (
        <div
          {...getRootProps()}
          style={{ width: '100%', height: '100%' }}
        >
          <div className="flex h-screen w-screen items-center justify-center bg-black">
            <div className="mx-auto max-w-xl space-y-4 rounded-xl border border-dashed border-green-600/50 bg-neutral-900 py-12 px-12 text-white shadow-lg drop-shadow-md">
              <div className="space-y-2 py-4 text-center">
                {loading ? (
                  <div className="flex flex-col items-center justify-center pt-8">
                    {LoadingIndicatorProgress && (
                      <LoadingIndicatorProgress className={'h-full w-full bg-black'} />
                    )}
                  </div>
                ) : (
                  <>
                    <p className="text-xl font-medium text-green-400">Upload a DICOM folder</p>
                    <p className="text-base text-neutral-200">
                      Choose a folder, then click <strong>Read DICOM</strong> to open the Basic
                      viewer.
                    </p>
                    <p className="text-base text-neutral-200">
                      Industry-level secure encryption in your browser.
                    </p>
                    <p className="text-base text-neutral-200">
                      <em className="italic text-white">Nothing ever leaves your local machine.</em>
                    </p>
                    <p className="text-sm text-green-500">
                      {pendingFiles.length > 0
                        ? `${pendingFiles.length} file(s) selected`
                        : 'No folder selected yet'}
                    </p>
                  </>
                )}
              </div>

              {!loading && (
                <>
                  <div className="flex flex-wrap justify-center gap-2 pt-2">
                    {getLoadButton(addChosenFiles, 'Select folder', true)}
                  </div>

                  <button
                    type="button"
                    disabled={pendingFiles.length === 0 || loading}
                    onClick={() => void loadIntoOhifLocal()}
                    className={`mt-4 w-full rounded-md py-3 text-center text-sm font-semibold shadow transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-black ${
                      pendingFiles.length > 0
                        ? 'cursor-pointer bg-green-500 text-white hover:bg-green-400 focus-visible:ring-green-500'
                        : 'cursor-not-allowed bg-neutral-800 text-neutral-600'
                    }`}
                  >
                    Read DICOM
                  </button>
                  <div className="mt-3 space-y-1 text-center text-xs leading-snug text-neutral-400">
                    <p>DICOM folders are supported.</p>
                    <p>ZIP files are not supported—only unzipped DICOM folders are supported.</p>
                  </div>

                  <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-left text-xs text-amber-300">
                    <span className="mt-0.5 text-base leading-none">⚠️</span>
                    <p>
                      <strong className="text-amber-200">Use Chrome or Edge for the best experience.</strong>{' '}
                      Other browsers may not support folder selection or the Save as Doc feature.
                      Do not switch browsers mid-session to avoid losing your work.
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </Dropzone>
  );
}

export default Local;
